import { Component, EventEmitter, Input, OnDestroy, OnInit, Optional, Output } from "@angular/core";
import { AbstractControl, FormArray, FormArrayName, FormGroup } from "@angular/forms";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

import { Command, IconCommand } from "@tylertech/commands-module";
import { LgdFormControlFactory, setEnabled } from "@tylertech/lgd-reactive-forms";
import { INote } from "@tylertech/tyler-api-types";
import { EventEmitterHelper } from "@tylertech/shared-module";

@Component({
    selector: "note",
    templateUrl: "./note.tmpl.html",
    standalone: false
})
export class NoteComponent implements OnInit, OnDestroy
{
    @Input() readonly hideSubject: boolean;
    @Input() readonly model: INote;
    @Input() readonly viewOnly: boolean;

    // FIXME(DO-7969) - Rename, outputs shouldn't be named with the "on" prefix. that prefix should be reserved for event handler functions at are attached to events/outputs
    @Output() protected readonly onDelete = new EventEmitter<INote>();

    private readonly destructor$ = new Subject<void>();

    readonly noteForm: FormGroup;

    readonly deleteCommand: Command;

    constructor(
        /** @deprecated - DO-7969 - there doesn't appear to be any usages where a formArrayName would be created. */
        @Optional() private readonly formArrayDirective?: FormArrayName)
    {
        this.noteForm = new FormGroup({
            subject: LgdFormControlFactory.text(
                undefined,
                true,
                {
                    maxLength: 50,
                }),
            description: LgdFormControlFactory.text(
                undefined,
                true,
                {
                    maxLength: 1000,
                }),
        });

        this.deleteCommand = new IconCommand({
            icon: "delete",
            tooltip: "Delete Note",
            action: () => this.deleteNote(),
            getEnabled: () => !this.viewOnly,
        });
    }

    /** @deprecated - DO-7969 - there doesn't appear to be any usages where a formArrayName would be created. */
    private get parentFormArray(): FormArray | undefined { return this.formArrayDirective?.control; }

    protected get subjectControl(): AbstractControl<unknown> { return this.noteForm.get("subject"); }
    protected get descriptionControl(): AbstractControl<unknown> { return this.noteForm.get("description"); }

    private get canDelete(): boolean { return EventEmitterHelper.hasSubscribers(this.onDelete); }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void
    {
        if (!this.model)
        {
            throw "note.component | Required binding missing: model";
        }

        // FIXME(DO-7969) - there doesn't appear to be any usages where a formArrayName would be created.
        if (this.parentFormArray)
        {
            this.parentFormArray.push(this.noteForm);
        }

        const self = this;

        setEnabled(this.subjectControl, !this.viewOnly)
            .setValue(this.model.Subject);
        subscribeToValueChanges(this.subjectControl, (newValue: string) =>
        {
            this.model.Subject = newValue;
        });

        setEnabled(this.descriptionControl, !this.viewOnly)
            .setValue(this.model.Description);
        subscribeToValueChanges(this.descriptionControl, (newValue: string) =>
        {
            this.model.Description = newValue;
        });

        function subscribeToValueChanges<T = unknown>(control: AbstractControl<T>, subscriptionFn: (newValue: T) => void): void
        {
            control.valueChanges
                .pipe(takeUntil(self.destructor$))
                .subscribe(x =>
                {
                    subscriptionFn(x);
                });
        }
    }

    protected deleteNote(): void
    {
        if (!this.canDelete)
        {
            return;
        }

        this.onDelete.emit(this.model);
    }
}
